/**
 * useCredits — reactive wrapper over credits-store (points-based system).
 *
 * Exposes point consumption, gate checks, plan state, and summary.
 * Re-renders on every credit/plan change.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getPlan,
  setPlan,
  canPerformAction,
  spendPoints,
  getPointsSummary,
  canAddCvVersion,
  canSaveJobOffer,
  isPremium,
  setPremium,
  savePointsStore,
  type ActionType,
  type Plan,
  type PointsSummary,
} from '../lib/credits-store';
import { useAuth } from '../contexts/AuthContext';
import { apiFetchGet } from '../lib/api-fetch';

export interface UseCreditsReturn {
  plan: Plan;
  premium: boolean;
  planExpiresAt: string | null;
  summary: PointsSummary;
  /** Check + spend points for an action. Returns false if quota exceeded. */
  tryConsume: (action: ActionType) => boolean;
  /** Check without consuming */
  canUse: (action: ActionType) => boolean;
  /** Check if a hard limit (cvVersions or jobOffers) is exceeded */
  isHardLimitExceeded: (type: 'cvVersions' | 'jobOffers', currentCount: number) => boolean;
  /** Upgrade plan */
  upgradePlan: (plan: Plan) => void;
  /** Unlock premium (shorthand for upgradePlan('premium')) */
  upgradeToPremium: () => void;
  refresh: () => void;
  /** Re-fetche plan + crédits depuis le serveur (après code promo par ex.) */
  refreshFromServer: () => Promise<void>;
}

export function useCredits(): UseCreditsReturn {
  const [plan, setPlanState] = useState<Plan>(() => getPlan());
  const [planExpiresAt, setPlanExpiresAt] = useState<string | null>(null);
  const [summary, setSummary] = useState<PointsSummary>(() => getPointsSummary());
  const { session } = useAuth();

  const applyServerProfile = useCallback(
    (json: { plan: Plan; plan_expires_at?: string | null; spent: number; earned: number; month: string }) => {
      setPlan(json.plan);
      savePointsStore({ month: json.month, spent: json.spent, earned: json.earned });
      setPlanState(json.plan);
      setPlanExpiresAt(json.plan_expires_at ?? null);
      setSummary(getPointsSummary());
    },
    [],
  );

  // Synchroniser plan + crédits depuis le serveur à chaque connexion.
  // Le serveur a autorité : écrase le localStorage (empêche la manipulation via DevTools).
  useEffect(() => {
    if (!session?.access_token) return;

    apiFetchGet<{ plan: Plan; plan_expires_at?: string | null; spent: number; earned: number; month: string }>(
      '/api/user/profile',
    )
      .then(({ json }) => { applyServerProfile(json); })
      .catch(() => {
        // Serveur inaccessible : conserver le localStorage (dégradation gracieuse offline)
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]); // Re-run uniquement lors d'un nouveau login

  const refreshFromServer = useCallback(async () => {
    try {
      const { json } = await apiFetchGet<{ plan: Plan; plan_expires_at?: string | null; spent: number; earned: number; month: string }>(
        '/api/user/profile',
      );
      applyServerProfile(json);
    } catch { /* ignore */ }
  }, [applyServerProfile]);

  const refresh = useCallback(() => {
    setPlanState(getPlan());
    setSummary(getPointsSummary());
  }, []);

  const tryConsume = useCallback((action: ActionType): boolean => {
    if (!canPerformAction(action)) return false;
    spendPoints(action);
    setSummary(getPointsSummary());
    return true;
  }, []);

  const canUse = useCallback((action: ActionType) => canPerformAction(action), []);

  const isHardLimitExceeded = useCallback((
    type: 'cvVersions' | 'jobOffers',
    currentCount: number,
  ) => {
    if (type === 'cvVersions') return !canAddCvVersion(currentCount);
    return !canSaveJobOffer(currentCount);
  }, []);

  const upgradePlan = useCallback((newPlan: Plan) => {
    setPlan(newPlan);
    setPlanState(newPlan);
    setSummary(getPointsSummary());
  }, []);

  const upgradeToPremium = useCallback(() => {
    setPremium(true);
    setPlanState('premium');
    setSummary(getPointsSummary());
  }, []);

  return {
    plan,
    premium: plan === 'premium',
    planExpiresAt,
    summary,
    tryConsume,
    canUse,
    isHardLimitExceeded,
    upgradePlan,
    upgradeToPremium,
    refresh,
    refreshFromServer,
  };
}

// Legacy re-exports used by FreemiumGate & other older callers
export { isPremium, setPremium };
export type { ActionType, Plan, PointsSummary };

// Legacy type alias — credits are now points, kept to avoid breaking callers that only import the type
export type CreditType = ActionType;
export type Credits = PointsSummary;
